// packages/ui/src/Avatar.tsx
import React from 'react';
import clsx from 'clsx';

// This is a minimal, temporary Avatar component to satisfy imports.
// It should ideally be a fully-fledged component within the @dopomogai/ui library.

interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  initials?: string;
  src?: string;
  alt?: string;
  size?: 'sm' | 'md' | 'lg'; // Add size prop for consistency
}

export const Avatar: React.FC<AvatarProps> = ({
  icon,
  initials,
  src,
  alt,
  size = 'md',
  className,
  ...props
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };

  return (
    <div
      className={clsx(
        'relative flex items-center justify-center rounded-full bg-slate-200 text-slate-700 font-semibold overflow-hidden flex-shrink-0',
        '[.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white/80',
        '[.theme-liquid-glass_&]:border [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:backdrop-blur-sm',
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {src ? (
        <img src={src} alt={alt || 'Avatar'} className="absolute inset-0 w-full h-full object-cover" />
      ) : icon ? (
        <span className="flex items-center justify-center h-full w-full">{icon}</span>
      ) : initials ? (
        <span>{initials.substring(0, 2).toUpperCase()}</span>
      ) : (
        // Fallback icon if no src, icon, or initials
        <svg
          className="h-2/3 w-2/3 text-slate-400 [.theme-liquid-glass_&]:text-white/40"
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM12 12.5c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
        </svg>
      )}
    </div>
  );
};
